import { styled } from '@mui/material/styles';
import { Box, Button, Typography, TableCell, TableContainer } from '@mui/material';

// ============================================
// 색상 토큰 (Figma 5001:8663 기준)
// ============================================
const COLORS = {
  dark: '#404040',
  teal: '#4ab3bc',
  tealBg: '#f3f9fa',
  grayBg: '#f6f7f9',
  border: '#d9d9d9',
  grayText: '#737373',
  textStrong: '#262626',
  green: '#55c982',
  orange: '#ee735a',
  white: '#ffffff',
};

// ============================================
// Loading / Table overlay (기존 유지)
// ============================================
export const LoadingScreen = styled(Box)({
  height: '100%',
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

export const TableWrapper = styled(Box)({
  position: 'relative',
  width: '100%',
});

export const TableLoadingOverlay = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(255, 255, 255, 0.7)',
  backdropFilter: 'blur(2px)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 10,
});

// ============================================
// 페이지 전체 레이아웃
// ============================================
export const PageContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  paddingTop: '20px',
  width: '100%',
});

// ============================================
// 상단 기간 바 (일일/주간/월간 세그먼트 + 기간 칩 하나 + 팝오버)
// 기간은 필터 패널에서 빼내 화면 맨 위 한 곳에서만 관리한다 — 날짜가 두 군데 나오지 않게.
// ============================================
export const PeriodBarRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  width: '100%',
});

export const PeriodToggleGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
});

export const PeriodToggleButton = styled(Box, {
  shouldForwardProp: prop => prop !== 'selected' && prop !== 'position',
})(({ selected, position }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '82px',
  height: '40px',
  fontSize: '16px',
  fontWeight: 500,
  cursor: 'pointer',
  userSelect: 'none',
  transition: 'all 0.13s ease',
  background: selected ? COLORS.dark : COLORS.white,
  color: selected ? COLORS.white : COLORS.dark,
  border: `1px solid ${COLORS.dark}`,
  // 가운데 버튼이 좌우 테두리 중복되지 않도록 처리
  borderLeftWidth: position === 'left' ? '1px' : 0,
  borderTopLeftRadius: position === 'left' ? '10px' : 0,
  borderBottomLeftRadius: position === 'left' ? '10px' : 0,
  borderTopRightRadius: position === 'right' ? '10px' : 0,
  borderBottomRightRadius: position === 'right' ? '10px' : 0,
}));

export const PeriodCenter = styled(Box)({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '12px',
});

// 우측 기준일. 세그먼트 그룹과 폭을 맞춰 기간 칩이 화면 중앙에 오게도 한다.
export const PeriodBaseLabel = styled(Typography)({
  width: '246px',
  flexShrink: 0,
  fontSize: '15px',
  color: COLORS.grayText,
  textAlign: 'right',
  whiteSpace: 'nowrap',
});

export const PeriodArrow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '40px',
  height: '40px',
  borderRadius: '10px',
  color: COLORS.grayText,
  fontSize: '26px',
  lineHeight: 1,
  cursor: 'pointer',
  userSelect: 'none',
  '&:hover': {
    background: '#f1f4f5',
    color: COLORS.textStrong,
  },
});

export const PeriodChipAnchor = styled(Box)({
  position: 'relative',
});

export const PeriodChip = styled(Box, {
  shouldForwardProp: prop => prop !== 'open',
})(({ open }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '11px',
  height: '44px',
  padding: '0 16px',
  borderRadius: '12px',
  background: COLORS.white,
  cursor: 'pointer',
  userSelect: 'none',
  transition: 'box-shadow 0.13s ease',
  border: `1px solid ${open ? COLORS.teal : COLORS.dark}`,
  '&:hover': {
    boxShadow: '0 0 0 3px rgba(74, 179, 188, 0.18)',
  },
}));

export const PeriodChipText = styled(Typography)({
  fontSize: '19px',
  fontWeight: 700,
  color: COLORS.textStrong,
  whiteSpace: 'nowrap',
  letterSpacing: '-0.01em',
});

export const PeriodChipDays = styled(Typography)({
  fontSize: '12px',
  fontWeight: 500,
  color: COLORS.grayText,
  whiteSpace: 'nowrap',
});

export const PeriodChipCaret = styled(Box)({
  fontSize: '9px',
  color: COLORS.teal,
});

export const PeriodPopover = styled(Box)({
  position: 'absolute',
  top: '52px',
  left: '50%',
  transform: 'translateX(-50%)',
  width: '308px',
  background: COLORS.white,
  border: '1px solid #dfe3e5',
  borderRadius: '16px',
  boxShadow: '0 16px 44px rgba(0, 0, 0, 0.20)',
  zIndex: 60,
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
});

export const PeriodPopHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
});

export const PeriodPopMonth = styled(Typography)({
  fontSize: '17px',
  fontWeight: 700,
  color: COLORS.teal,
  letterSpacing: '-0.01em',
  whiteSpace: 'nowrap',
});

export const PeriodPopNav = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '2px',
});

export const PeriodPopNavBtn = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '28px',
  height: '28px',
  borderRadius: '8px',
  color: COLORS.grayText,
  fontSize: '18px',
  lineHeight: 1,
  cursor: 'pointer',
  userSelect: 'none',
  '&:hover': {
    background: '#f4f6f7',
    color: COLORS.textStrong,
  },
});

// 연한 트랙 위에 흰 카드가 올라앉는 세그먼트 컨트롤 — 탭(위치)과 칩(값)의 시각 언어를 분리한다.
export const SegmentTrack = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '3px',
  padding: '3px',
  background: '#f1f4f5',
  borderRadius: '10px',
});

export const PeriodModeTab = styled(Box, {
  shouldForwardProp: prop => prop !== 'selected',
})(({ selected }) => ({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '32px',
  borderRadius: '8px',
  fontSize: '13px',
  fontWeight: 700,
  cursor: 'pointer',
  userSelect: 'none',
  whiteSpace: 'nowrap',
  transition: 'all 0.14s ease',
  background: selected ? COLORS.white : 'transparent',
  color: selected ? COLORS.textStrong : '#525252',
  border: `1px solid ${selected ? '#dcdcdc' : 'transparent'}`,
  boxShadow: selected ? '0 1px 3px rgba(0, 0, 0, 0.10)' : 'none',
}));

export const PeriodCalWrap = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
});

export const PeriodCalRow = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
  gap: '2px',
});

export const PeriodCalWeekCell = styled(Box, {
  shouldForwardProp: prop => prop !== 'tone',
})(({ tone }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '24px',
  fontSize: '11px',
  fontWeight: 700,
  color: tone === 'sun' ? '#c0392b' : tone === 'sat' ? '#2b6cb0' : COLORS.grayText,
}));

export const PeriodCalCell = styled(Box, {
  shouldForwardProp: prop => !['inMonth', 'inRange', 'isEdge', 'tone'].includes(prop),
})(({ inMonth, inRange, isEdge, tone }) => {
  const baseColor = tone === 'sun' ? '#c0392b' : tone === 'sat' ? '#2b6cb0' : COLORS.textStrong;
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '34px',
    fontSize: '13px',
    userSelect: 'none',
    transition: 'background 0.1s ease',
    // 이번 달이 아닌 날은 맥락으로만 보여준다 — 눌러도 반응하지 않는다.
    cursor: inMonth ? 'pointer' : 'default',
    color: isEdge ? COLORS.white : inMonth ? baseColor : '#767676',
    background: isEdge ? COLORS.teal : inRange ? '#eaf6f7' : 'transparent',
    borderRadius: isEdge ? '999px' : '8px',
    fontWeight: isEdge ? 700 : inRange ? 600 : 500,
  };
});

export const PeriodDialWrap = styled(Box)({
  position: 'relative',
  height: '200px',
});

export const PeriodDialHighlight = styled(Box)({
  position: 'absolute',
  left: 0,
  right: 0,
  top: '80px',
  height: '40px',
  background: '#f1f4f5',
  borderRadius: '11px',
});

export const PeriodDialCols = styled(Box)({
  position: 'relative',
  display: 'flex',
  height: '200px',
});

export const PeriodDialCol = styled(Box)({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
});

// 다이얼은 ▲▼ 없이 옆 값을 눌러 움직이므로, 흐리게 두면 조작 자체가 안 보인다.
export const PeriodDialItem = styled(Box, {
  shouldForwardProp: prop => prop !== 'distance',
})(({ distance }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '40px',
  cursor: 'pointer',
  userSelect: 'none',
  whiteSpace: 'nowrap',
  transition: 'color 0.12s ease',
  fontSize: distance === 0 ? '17px' : '15px',
  fontWeight: distance === 0 ? 700 : 500,
  color: distance === 0 ? COLORS.textStrong : distance === 1 ? '#525252' : COLORS.grayText,
}));

export const PeriodManualBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  paddingTop: '2px',
  borderTop: '1px solid #eef0f1',
});

export const PeriodManualHead = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  paddingTop: '8px',
});

export const PeriodManualLabel = styled(Typography)({
  fontSize: '11px',
  fontWeight: 700,
  color: COLORS.grayText,
  letterSpacing: '0.04em',
  whiteSpace: 'nowrap',
});

export const PeriodManualHint = styled(Typography)({
  fontSize: '11px',
  fontWeight: 600,
  color: '#1f6a70',
  whiteSpace: 'nowrap',
});

export const PeriodManualRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '7px',
});

export const PeriodInputBox = styled(Box, {
  shouldForwardProp: prop => prop !== 'active',
})(({ active }) => ({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  height: '36px',
  borderRadius: '8px',
  overflow: 'hidden',
  transition: 'all 0.13s ease',
  border: `1px solid ${active ? COLORS.teal : '#dfe3e5'}`,
  background: active ? COLORS.tealBg : '#fbfcfc',
}));

export const PeriodInput = styled('input')({
  flex: 1,
  minWidth: 0,
  height: '100%',
  padding: '0 2px 0 8px',
  border: 'none',
  background: 'transparent',
  fontSize: '13px',
  fontWeight: 600,
  color: COLORS.textStrong,
  textAlign: 'center',
  outline: 'none',
});

export const PeriodAimBtn = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '30px',
  height: '100%',
  flexShrink: 0,
  cursor: 'pointer',
  borderLeft: '1px solid #eef0f1',
  '&:hover': {
    background: '#eaf6f7',
  },
});

export const PeriodManualTilde = styled(Typography)({
  color: COLORS.grayText,
  fontSize: '13px',
  flexShrink: 0,
});

export const PeriodConfirmBtn = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '44px',
  borderRadius: '11px',
  background: COLORS.teal,
  color: COLORS.white,
  fontSize: '14px',
  fontWeight: 700,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  '&:hover': {
    boxShadow: '0 0 0 3px rgba(74, 179, 188, 0.24)',
  },
});

// ============================================
// 필터 + 대시보드 가로 배치
// ============================================
export const FilterDashboardRow = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '32px',
  // 대시보드는 자기 내용 높이만 쓴다. stretch 면 필터 패널 높이를 따라 카드가 세로로 늘어났다.
  alignItems: 'flex-start',
  width: '100%',
});

// ============================================
// 좌측 필터 패널 (카테고리 세그먼트 탭)
// 탭으로 나눠 담아 필터가 늘어도 패널 높이와 대시보드 위치가 변하지 않는다.
// ============================================
export const FilterPanelCard = styled(Box)({
  width: '480px',
  flexShrink: 0,
  alignSelf: 'flex-start',
  display: 'flex',
  flexDirection: 'column',
  background: COLORS.white,
  border: '1px solid #dfe3e5',
  borderRadius: '16px',
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
});

export const FilterTabBar = styled(Box)({
  padding: '14px 16px 11px',
});

// 비활성 라벨·뱃지는 흰 배경이 아니라 연한 트랙 위에 앉으므로 한 단계 진하게 잡는다.
export const FilterTab = styled(Box, {
  shouldForwardProp: prop => prop !== 'selected',
})(({ selected }) => ({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '5px',
  height: '38px',
  padding: '0 8px',
  borderRadius: '9px',
  fontSize: '14px',
  fontWeight: 700,
  cursor: 'pointer',
  userSelect: 'none',
  whiteSpace: 'nowrap',
  transition: 'all 0.15s ease',
  background: selected ? COLORS.white : 'transparent',
  color: selected ? COLORS.textStrong : '#525252',
  border: `1px solid ${selected ? '#dcdcdc' : 'transparent'}`,
  boxShadow: selected ? '0 1px 3px rgba(0, 0, 0, 0.10)' : 'none',
  '&:hover': {
    color: COLORS.textStrong,
  },
}));

export const FilterTabLabel = styled('span')({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

export const FilterTabBadge = styled('span', {
  shouldForwardProp: prop => prop !== 'selected',
})(({ selected }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '18px',
  height: '18px',
  padding: '0 5px',
  borderRadius: '9px',
  fontSize: '11px',
  fontWeight: 700,
  flexShrink: 0,
  background: selected ? COLORS.teal : COLORS.white,
  color: selected ? COLORS.white : '#525252',
}));

// 최소 높이를 두지 않는다 — 탭마다 자기 줄 수만큼만(상품 탭 빈 공간 제거, 사용자 요청).
// 대시보드가 패널보다 높아 탭을 옮겨 패널 높이가 바뀌어도 아래 표 위치는 그대로다.
export const FilterFieldList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  padding: '2px 16px 16px',
});

export const FilterFieldRow = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '10px',
});

// 필드 이름 열은 가운데 정렬(사용자 요청) — '상품'처럼 짧은 이름이 왼쪽에 붙어 긴 이름과 따로 놀았다.
export const FilterFieldLabel = styled(Typography)({
  width: '76px',
  textAlign: 'center',
  flexShrink: 0,
  paddingTop: '7px',
  fontSize: '14px',
  fontWeight: 600,
  color: COLORS.dark,
  lineHeight: 1.35,
  wordBreak: 'keep-all',
});

export const FilterFieldControl = styled(Box)({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
});

export const ChipGroup = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '6px',
  minWidth: 0,
});

export const ChipToggleButton = styled('button')({
  flexShrink: 0,
  width: '32px',
  height: '32px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  borderRadius: '999px',
  border: `1px solid ${COLORS.border}`,
  background: COLORS.white,
  color: '#737373',
  cursor: 'pointer',
  '&:hover': { borderColor: COLORS.teal, color: COLORS.teal },
});

export const FilterChip = styled(Box, {
  shouldForwardProp: prop => prop !== 'selected',
})(({ selected }) => ({
  display: 'flex',
  alignItems: 'center',
  height: '32px',
  padding: '0 12px',
  borderRadius: '999px',
  fontSize: '13.5px',
  fontWeight: 500,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  userSelect: 'none',
  transition: 'all 0.13s ease',
  background: selected ? COLORS.dark : COLORS.white,
  color: selected ? COLORS.white : COLORS.dark,
  border: `1px solid ${selected ? COLORS.dark : COLORS.border}`,
  '&:hover': {
    boxShadow: '0 0 0 3px rgba(74, 179, 188, 0.16)',
  },
}));

export const FieldNote = styled('span')({
  fontSize: '12px',
  color: COLORS.grayText,
  lineHeight: 1.5,
});

// ── 패널 상단 통합 검색(#168) ──
// 검색창 + 상품 선택 — 탭 밖 '찾기' 영역
export const FilterSearchRow = styled(Box)({
  padding: '16px 16px 0',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

export const FilterSearchBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  height: '42px',
  padding: '0 12px',
  border: `1px solid ${COLORS.border}`,
  borderRadius: '9px',
  background: COLORS.white,
  transition: 'all 0.13s ease',
  '&:focus-within': {
    borderColor: COLORS.teal,
    background: '#fbfeff',
  },
});

export const FilterSearchInput = styled('input')({
  flex: 1,
  minWidth: 0,
  border: 0,
  outline: 'none',
  background: 'transparent',
  fontSize: '14px',
  color: COLORS.textStrong,
  '&::placeholder': { color: '#a3a3a3' },
  '&::-webkit-search-cancel-button': { display: 'none' },
});

export const FilterSearchClear = styled('button')({
  width: '20px',
  height: '20px',
  flexShrink: 0,
  border: 0,
  borderRadius: '5px',
  background: '#eef1f2',
  color: '#525252',
  fontSize: '13px',
  lineHeight: 1,
  cursor: 'pointer',
  padding: 0,
});

// 상품 드롭다운 안 '판매종료 숨기기' 토글
export const ListToggleRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  padding: '7px 9px',
  borderRadius: '8px',
  background: '#f1f4f5',
});

export const ListToggleText = styled('span')({
  fontSize: '12.5px',
  fontWeight: 600,
  color: COLORS.dark,
  '& small': {
    display: 'block',
    marginTop: '1px',
    fontSize: '11px',
    fontWeight: 400,
    color: COLORS.grayText,
  },
});

export const ListToggleSwitch = styled('button', {
  shouldForwardProp: prop => prop !== 'checked',
})(({ checked }) => ({
  position: 'relative',
  width: '34px',
  height: '20px',
  flexShrink: 0,
  border: 0,
  borderRadius: '10px',
  padding: 0,
  cursor: 'pointer',
  background: checked ? COLORS.teal : '#c9cfd2',
  transition: 'background 0.15s ease',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '3px',
    left: checked ? '17px' : '3px',
    width: '14px',
    height: '14px',
    borderRadius: '50%',
    background: COLORS.white,
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
    transition: 'left 0.15s ease',
  },
}));

// 상품 행 — 왼쪽 종류 선택 + 오른쪽 상품 드롭다운
export const ProductPickRow = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '6px',
  minWidth: 0,
  // 두 번째 자식 = 상품 드롭다운. 남는 폭을 다 쓴다(선택한 상품명이 박스 안에 들어간다).
  '& > *:nth-child(2)': { flex: 1 },
});

export const ProductCategorySelect = styled('select')({
  flexShrink: 0,
  height: '34px',
  padding: '0 20px 0 8px',
  borderRadius: '9px',
  border: `1px solid ${COLORS.border}`,
  background: `${COLORS.white} url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='5'%3E%3Cpath d='M0 0l4 5 4-5z' fill='%238c8c8c'/%3E%3C/svg%3E") no-repeat right 8px center`,
  appearance: 'none',
  fontSize: '12.5px',
  color: COLORS.dark,
  cursor: 'pointer',
  outline: 'none',
  '&:focus': { borderColor: COLORS.teal },
});

// 상품 드롭다운 — 지점명(여러 지점이 섞일 때)과 초성 구분선
export const ListItemSub = styled('span')({
  flexShrink: 0,
  marginLeft: 'auto',
  fontSize: '11.5px',
  color: '#8c8c8c',
  whiteSpace: 'nowrap',
});

export const ListInitialHeader = styled(Box)({
  margin: '4px 8px 2px',
  paddingBottom: '2px',
  borderBottom: '1px solid #eef1f2',
  fontSize: '11px',
  fontWeight: 700,
  color: '#4ab3bc',
});

export const EndedTag = styled('span')({
  flexShrink: 0,
  marginLeft: 'auto',
  padding: '1px 5px',
  borderRadius: '4px',
  fontSize: '10.5px',
  fontWeight: 700,
  background: '#fbe0da',
  color: '#c0392b',
});

// ── 긴 목록(방문경로·판매담당자)용 검색형 드롭다운 ──
export const ListSelectAnchor = styled(Box)({
  position: 'relative',
  minWidth: 0,
  // 전체 폭을 다 먹지 않게 — 값이 짧은데 컨트롤만 긴 게 제일 허전하다.
  alignSelf: 'flex-start',
});

export const ListSelectBox = styled(Box, {
  shouldForwardProp: prop => prop !== 'active',
})(({ active }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  height: '34px',
  padding: '0 12px',
  borderRadius: '9px',
  cursor: 'pointer',
  fontSize: '14px',
  color: COLORS.dark,
  minWidth: '150px',
  transition: 'all 0.13s ease',
  border: `1px solid ${active ? COLORS.teal : COLORS.border}`,
  background: active ? '#fbfeff' : COLORS.white,
  '&:hover': {
    boxShadow: '0 0 0 3px rgba(74, 179, 188, 0.14)',
  },
}));

// 남는 폭을 다 쓰고 길면 말줄임. 기본은 아이콘 옆 왼쪽 정렬, centered 면 가운데(넓은 상품 박스 — 사용자 요청).
// 미선택(전체)은 연한 글씨로 '아직 안 골랐음'을 보인다.
export const ListSelectLabel = styled('span', {
  shouldForwardProp: prop => prop !== 'placeholder' && prop !== 'centered',
})(({ placeholder, centered }) => ({
  flex: 1,
  minWidth: 0,
  textAlign: centered ? 'center' : 'left',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  color: placeholder ? '#8c8c8c' : COLORS.textStrong,
  fontWeight: placeholder ? 400 : 500,
}));

export const ListSelectCaret = styled('span')({
  color: COLORS.teal,
  fontSize: '9px',
  flexShrink: 0,
});

// 목록 팝오버 안 묶음 헤더 — 16개를 한 줄로 늘어놓으면 안 읽힌다.
export const ListGroupHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '8px 8px 4px',
  fontSize: '11px',
  fontWeight: 700,
  color: '#8c8c8c',
  letterSpacing: '0.02em',
  '& svg': { fontSize: '15px', color: COLORS.teal },
});

export const ListPopover = styled(Box, {
  shouldForwardProp: prop => prop !== 'wide',
})(({ wide }) => ({
  position: 'absolute',
  top: '38px',
  left: 0,
  // 상품명은 길어서 상품 드롭다운만 넓힌다(패널 컨트롤 폭 안).
  width: wide ? '340px' : '272px',
  background: COLORS.white,
  border: '1px solid #dfe3e5',
  borderRadius: '12px',
  boxShadow: '0 12px 32px rgba(0, 0, 0, 0.16)',
  zIndex: 30,
  padding: '11px',
  display: 'flex',
  flexDirection: 'column',
  gap: '9px',
}));

export const ListPopHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  padding: '0 2px',
});

export const ListPopCount = styled(Typography)({
  fontSize: '12px',
  fontWeight: 700,
  color: COLORS.textStrong,
});

export const ListPopActions = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const ListPopAction = styled('span', {
  shouldForwardProp: prop => prop !== 'muted',
})(({ muted }) => ({
  fontSize: '11px',
  fontWeight: 600,
  color: muted ? '#8c8c8c' : '#1f6a70',
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'underline',
  },
}));

export const ListPopDivider = styled('span')({
  width: '1px',
  height: '10px',
  background: '#dfe3e5',
});

export const ListSearchInput = styled('input')({
  height: '31px',
  padding: '0 10px',
  border: '1px solid #dfe3e5',
  borderRadius: '7px',
  fontSize: '13px',
  outline: 'none',
  width: '100%',
  background: '#fbfcfc',
  '&:focus': {
    borderColor: COLORS.teal,
    background: COLORS.white,
  },
});

export const ListItems = styled(Box)({
  maxHeight: '240px',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: '1px',
  margin: '0 -3px',
  padding: '0 3px',
});

export const ListItemRow = styled(Box, {
  shouldForwardProp: prop => prop !== 'selected',
})(({ selected }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '9px',
  padding: '7px 8px',
  borderRadius: '7px',
  cursor: 'pointer',
  fontSize: '13px',
  color: COLORS.textStrong,
  background: selected ? COLORS.tealBg : 'transparent',
  fontWeight: selected ? 600 : 400,
  '&:hover': {
    background: '#f4f6f7',
  },
}));

// 여러 개 고를 수 있으면 체크박스, 하나만 고르면 라디오 — 눌러보지 않아도 알게.
export const ListItemMark = styled('span', {
  shouldForwardProp: prop => prop !== 'selected' && prop !== 'multi',
})(({ selected, multi }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '16px',
  height: '16px',
  flexShrink: 0,
  color: COLORS.white,
  fontSize: '9px',
  lineHeight: 1,
  borderRadius: multi ? '4px' : '999px',
  border: `1px solid ${selected ? COLORS.teal : COLORS.border}`,
  background: selected ? COLORS.teal : COLORS.white,
}));

export const ListItemLabel = styled('span')({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const ListDoneBtn = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '32px',
  borderRadius: '7px',
  background: COLORS.dark,
  color: COLORS.white,
  fontSize: '13px',
  fontWeight: 600,
  cursor: 'pointer',
});

export const SelectedChipRow = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '5px',
});

export const SelectedChip = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '5px',
  height: '25px',
  padding: '0 4px 0 9px',
  borderRadius: '7px',
  background: COLORS.tealBg,
  border: '1px solid #cfe9ec',
  fontSize: '12px',
  fontWeight: 500,
  color: '#1f6a70',
});

export const SelectedChipRemove = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '15px',
  height: '15px',
  borderRadius: '4px',
  background: '#e3f0f2',
  fontSize: '10px',
  cursor: 'pointer',
});

// ── 요약 줄 + 액션 ──
export const FilterFooter = styled(Box)({
  padding: '10px 16px 13px',
  borderTop: '1px solid #eef0f1',
  background: '#fbfcfc',
  borderRadius: '0 0 15px 15px',
  display: 'flex',
  flexDirection: 'column',
  gap: '9px',
});

export const FilterSummaryRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  flexWrap: 'wrap',
});

export const FilterStatusPill = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  height: '23px',
  padding: '0 9px',
  borderRadius: '6px',
  fontSize: '11px',
  fontWeight: 700,
  whiteSpace: 'nowrap',
  flexShrink: 0,
  background: COLORS.tealBg,
  color: '#1f6a70',
});

export const FilterTokenChip = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '5px',
  height: '23px',
  padding: '0 7px 0 9px',
  borderRadius: '6px',
  background: '#eef1f2',
  fontSize: '12px',
  whiteSpace: 'nowrap',
  minWidth: 0,
  overflow: 'hidden',
});

export const FilterTokenField = styled('span')({
  color: '#525252',
  flexShrink: 0,
});

export const FilterTokenValue = styled('span')({
  color: COLORS.textStrong,
  fontWeight: 600,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

export const FilterTokenRemove = styled('span')({
  color: '#a6adb1',
  fontSize: '13px',
  lineHeight: 1,
  cursor: 'pointer',
  flexShrink: 0,
  '&:hover': {
    color: COLORS.dark,
  },
});

// +N 은 감추기가 아니라 접힘 — 누르면 전부 펼쳐진다.
export const FilterMoreBtn = styled(Box, {
  shouldForwardProp: prop => prop !== 'collapse',
})(({ collapse }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  height: '23px',
  padding: '0 9px',
  borderRadius: '6px',
  background: COLORS.white,
  border: `1px solid ${collapse ? '#dfe3e5' : '#cfe9ec'}`,
  fontSize: '11px',
  fontWeight: 700,
  color: collapse ? COLORS.grayText : '#1f6a70',
  cursor: 'pointer',
  flexShrink: 0,
  whiteSpace: 'nowrap',
  '&:hover': {
    background: collapse ? COLORS.white : COLORS.tealBg,
    borderColor: collapse ? '#b9c0c3' : COLORS.teal,
    color: collapse ? COLORS.dark : '#1f6a70',
  },
}));

export const FilterActions = styled(Box)({
  display: 'flex',
  gap: '7px',
});

export const SearchButton = styled(Box, {
  shouldForwardProp: prop => prop !== 'disabled',
})(({ disabled }) => ({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '39px',
  borderRadius: '8px',
  background: COLORS.teal,
  color: COLORS.white,
  fontSize: '14px',
  fontWeight: 700,
  cursor: disabled ? 'default' : 'pointer',
  opacity: disabled ? 0.6 : 1,
  transition: 'box-shadow 0.15s ease',
  '&:hover': {
    boxShadow: disabled ? 'none' : '0 0 0 3px rgba(74, 179, 188, 0.24)',
  },
}));

export const ResetButton = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '84px',
  height: '39px',
  borderRadius: '8px',
  background: COLORS.white,
  border: '1px solid #dfe3e5',
  color: '#525252',
  fontSize: '13px',
  fontWeight: 600,
  cursor: 'pointer',
  '&:hover': {
    borderColor: '#b9c0c3',
    color: COLORS.textStrong,
  },
});

// ============================================
// 우측 대시보드 카드
// ============================================
export const DashboardArea = styled(Box)({
  // 폭이 부족하면 FilterDashboardRow가 wrap되어 대시보드가 필터 아래로 내려간다(720px 미만에서).
  flex: '1 1 720px',
  minWidth: 0,
  display: 'flex',
  flexWrap: 'wrap',
  gap: '24px',
  alignItems: 'stretch',
});

// grow: 디자인 폭 비율(380:338:338)을 flex-grow 가중치로 사용해 남은 공간을 비율대로 채운다.
export const DashboardColumn = styled(Box, {
  shouldForwardProp: prop => prop !== 'grow',
})(({ grow }) => ({
  // 기본 폭 220px 확보. 대시보드 폭이 더 좁아지면 컬럼끼리도 wrap되어 줄이 나뉜다.
  flex: `${grow || 338} 1 220px`,
  minWidth: '220px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
}));

// 대형 카드 (결제금액 / 신규등록 / 재등록)
export const BigCard = styled(Box, {
  shouldForwardProp: prop => prop !== 'variant',
})(({ variant }) => ({
  // 내용 높이가 기본. 대시보드가 필터 패널 높이를 따라가지 않으므로 basis 0 이면 카드가 눌려 글자가 겹친다.
  flex: '1 0 auto',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '16px',
  background: variant === 'teal' ? COLORS.tealBg : COLORS.grayBg,
  // teal 변형(결제금액)은 가운데 정렬, 회색 변형(신규/재등록)은 좌측 정렬
  ...(variant === 'teal'
    ? {
        gap: '32px',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '32px 24px',
      }
    : {
        gap: '16px',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '20px 24px',
      }),
}));

export const BigCardCenterGroup = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
  alignItems: 'center',
});

export const BigCardTitle = styled(Typography, {
  shouldForwardProp: prop => prop !== 'accent',
})(({ accent }) => ({
  fontSize: '24px',
  fontWeight: 600,
  textAlign: 'center',
  whiteSpace: 'nowrap',
  color: accent === 'teal' ? COLORS.teal : COLORS.grayText,
}));

export const BigCardAmountTeal = styled(Typography)({
  fontSize: '36px',
  fontWeight: 600,
  color: COLORS.teal,
  textAlign: 'center',
  width: '100%',
  whiteSpace: 'nowrap',
});

export const BigCardAmountDark = styled(Typography)({
  fontSize: '32px',
  fontWeight: 600,
  color: COLORS.dark,
  whiteSpace: 'nowrap',
});

export const BigCardCount = styled(Typography)({
  fontSize: '24px',
  fontWeight: 500,
  color: COLORS.grayText,
  textAlign: 'center',
  whiteSpace: 'nowrap',
});

export const BigCardCompare = styled(Box)({
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
  justifyContent: 'center',
});

export const BigCardCompareText = styled(Typography)({
  fontSize: '18px',
  fontWeight: 500,
  color: COLORS.grayText,
  whiteSpace: 'nowrap',
});

// 증감 방향 화살표 래퍼. 증가(up)=주황(Sub/3) 위쪽, 감소(down)=파랑 아래쪽(상하반전).
// svg fill=currentColor 가 이 color 를 받는다. 파랑은 금액색 teal(#4ab3bc)과 구분되는 #3b82f6.
export const CompareArrow = styled(Box, {
  shouldForwardProp: prop => prop !== 'direction',
})(({ direction }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  lineHeight: 0,
  color: direction === 'down' ? '#3b82f6' : COLORS.orange,
  transform: direction === 'down' ? 'scaleY(-1)' : 'none',
}));

// 소형 카드 (할인/결제예정/체험권/환불/양도/미수)
export const SmallCard = styled(Box, {
  shouldForwardProp: prop => prop !== 'variant',
})(({ variant }) => ({
  display: 'flex',
  // 금액이 길어 한 줄에 안 들어가면 이름 아래 줄로 내린다(이름과 금액이 붙어 겹치던 문제).
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  columnGap: '12px',
  rowGap: '2px',
  minHeight: '72px',
  padding: '16px 24px',
  borderRadius: '16px',
  background: variant === 'teal' ? COLORS.tealBg : COLORS.grayBg,
}));

export const SmallCardLabel = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  whiteSpace: 'nowrap',
});

export const SmallCardLabelText = styled(Typography, {
  shouldForwardProp: prop => prop !== 'tone',
})(({ tone }) => {
  const colorMap = {
    gray: COLORS.grayText,
    green: COLORS.green,
    orange: COLORS.orange,
  };
  return {
    fontSize: '18px',
    fontWeight: 600,
    color: colorMap[tone] || COLORS.grayText,
    whiteSpace: 'nowrap',
  };
});

export const SmallCardValue = styled(Typography)({
  marginLeft: 'auto',
  fontSize: '24px',
  fontWeight: 600,
  color: COLORS.dark,
  whiteSpace: 'nowrap',
});

// ============================================
// 테이블 영역
// ============================================
export const TableSection = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  width: '100%',
});

export const TableToolbar = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
});

export const TableTabs = styled(Box)({
  display: 'flex',
  alignItems: 'center',
});

export const TableTab = styled(Box, {
  shouldForwardProp: prop => prop !== 'selected',
})(({ selected }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '44px',
  padding: '0 16px',
  fontSize: '20px',
  fontWeight: 600,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  color: selected ? COLORS.teal : COLORS.grayText,
  borderBottom: selected ? `2px solid ${COLORS.teal}` : '2px solid transparent',
}));

export const TableToolbarRight = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

// 데이터 테이블 스크롤 컨테이너 (가로/세로 스크롤 + 커스텀 스크롤바)
// height 고정 제거 → 행 수에 맞춰 컨테이너가 줄어들어 하단 가로 스크롤바가 마지막 행 바로 밑에 온다.
// 행이 많아 maxHeight(540px)를 넘으면 세로 스크롤이 생긴다.
export const TableScroll = styled(TableContainer)({
  borderRadius: '5px',
  overflowX: 'auto',
  overflowY: 'auto',
  width: '100%',
  maxHeight: '540px',
  border: '1px solid #e1e1e1',
  '&::-webkit-scrollbar': { width: '8px', height: '8px' },
  '&::-webkit-scrollbar-track': { backgroundColor: '#f1f1f1', borderRadius: '10px' },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#c1c1c1',
    borderRadius: '10px',
    '&:hover': { backgroundColor: '#a8a8a8' },
  },
  '&::-webkit-scrollbar-corner': { backgroundColor: '#f1f1f1' },
});

export const HeadCell = styled(TableCell, {
  shouldForwardProp: prop => prop !== 'cellWidth',
})(({ cellWidth }) => ({
  borderRight: '1px solid #ddd',
  fontWeight: 500,
  fontSize: '12px',
  textAlign: 'center',
  ...(cellWidth && { width: cellWidth }),
}));

export const BodyCell = styled(TableCell, {
  shouldForwardProp: prop => prop !== 'tone',
})(({ tone }) => ({
  borderRight: '1px solid #ddd',
  fontSize: '12px',
  textAlign: 'center',
  color: tone === 'minus' ? '#ee735a' : 'inherit',
}));

export const MemberNameText = styled(Typography, {
  shouldForwardProp: prop => prop !== 'clickable',
})(({ clickable }) => ({
  fontSize: '12px',
  // 클릭 시 회원 결제정보(회원상세 결제탭)로 이동 → 밑줄·색상으로 클릭 가능함을 상시 노출.
  color: clickable ? '#4AB3BC' : 'inherit',
  textDecoration: clickable ? 'underline' : 'none',
  textUnderlineOffset: '2px',
  cursor: clickable ? 'pointer' : 'default',
  '&:hover': {
    fontWeight: clickable ? 600 : 'inherit',
  },
}));

// 테이블 내 작은 액션 버튼
export const RowActionButton = styled(Button, {
  shouldForwardProp: prop => prop !== 'tone',
})(({ tone }) => {
  const palette = {
    gray: { bg: '#808080', color: '#000000', hover: '#707070' },
    dark: { bg: '#737373', color: '#FFFFFF', hover: '#5f5f5f' },
    outline: { bg: '#ffffff', color: '#000000', hover: '#000000' },
  };
  const p = palette[tone] || palette.dark;
  return {
    borderRadius: '8px',
    minWidth: 'auto',
    padding: '4px 8px',
    fontSize: '12px',
    boxShadow: 'none',
    background: p.bg,
    color: p.color,
    border: tone === 'outline' ? '1px solid #000' : `1px solid ${p.bg}`,
    '&:hover': {
      boxShadow: 'none',
      background: tone === 'outline' ? '#000' : p.hover,
      color: tone === 'outline' ? '#fff' : p.color,
    },
  };
});

// ============================================
// 상품별 내역 — 그룹형(확장/축소) 테이블
// ============================================
export const ProductTableScroll = styled(Box)({
  width: '100%',
  overflowX: 'auto',
  '&::-webkit-scrollbar': { height: '8px' },
  '&::-webkit-scrollbar-track': { backgroundColor: '#f1f1f1', borderRadius: '10px' },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#c1c1c1',
    borderRadius: '10px',
    '&:hover': { backgroundColor: '#a8a8a8' },
  },
});

export const ProductTableInner = styled(Box, {
  shouldForwardProp: prop => prop !== 'tableWidth',
})(({ tableWidth }) => ({
  minWidth: tableWidth || '1552px',
  display: 'flex',
  flexDirection: 'column',
}));

// 결제 상태별 행 배경 톤. 금액이 아니라 '성격'을 구분하는 용도라 아주 옅게만 깐다
// (셀 텍스트 색 tone='refund'/'minus' 와 겹쳐도 읽히도록).
const ROW_TONE_BG = {
  refund: '#fdf3f1', // 환불 행 — 붉은 기
  outstanding: '#fff8ef', // 미수금 잔액이 남은 행 — 주황 기
};

export const PTRow = styled(Box, {
  shouldForwardProp: prop => prop !== 'variant' && prop !== 'clickable' && prop !== 'rowTone',
})(({ variant, clickable, rowTone }) => {
  const bgMap = {
    head: '#f2f2f2',
    group: '#ffffff',
    detailHead: '#f2f2f2',
    detail: '#f3f9fa',
    flat: '#ffffff', // 평면 테이블(환불/미수금 내역) 행 — 흰색 배경
    subtotal: '#eef4f5', // 그룹 소계 (detail 보다 살짝 진한 teal 톤)
    grandTotal: '#e6e6e6', // 전체 총계 (가장 진한 회색으로 구분)
  };
  return {
    display: 'flex',
    alignItems: 'stretch',
    width: '100%',
    background: ROW_TONE_BG[rowTone] || bgMap[variant] || '#ffffff',
    borderLeft: '1px solid #d9d9d9',
    borderRight: '1px solid #d9d9d9',
    borderBottom: '1px solid #d9d9d9',
    cursor: clickable ? 'pointer' : 'default',
    ...(variant === 'head' && {
      borderTop: '1px solid #d9d9d9',
      borderTopLeftRadius: '8px',
      borderTopRightRadius: '8px',
    }),
  };
});

export const PTCell = styled(Box, {
  shouldForwardProp: prop =>
    prop !== 'cellWidth' &&
    prop !== 'align' &&
    prop !== 'tone' &&
    prop !== 'bold' &&
    prop !== 'flex' &&
    prop !== 'nowrap',
})(({ cellWidth, align, tone, bold, flex, nowrap }) => ({
  // flex 칸도 cellWidth 를 주면 그만큼은 확보한다 — 고정 칸 합이 표 폭을 넘으면 flex 칸이 0 으로 눌려
  // 옆 칸과 숫자가 겹쳤다(상품별·종목별 내역 합계금액).
  ...(flex ? { flex: '1 1 0', minWidth: cellWidth || 0 } : { width: cellWidth, flexShrink: 0 }),
  display: 'flex',
  alignItems: 'center',
  justifyContent: align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : 'center',
  padding: '12px 16px',
  borderRight: '1px solid #d9d9d9',
  fontSize: '14px',
  fontWeight: bold ? 600 : 500,
  color: tone === 'refund' || tone === 'minus' ? '#ee735a' : '#262626',
  textAlign: align || 'center',
  // nowrap 컬럼(예: 지점명)은 줄바꿈 없이 한 줄 유지하고 넘치면 말줄임.
  // 그 외는 keep-all 로 한글 단어 중간 끊김(예: "신규/재등록" 깨짐) 방지 — 공백에서만 줄바꿈.
  ...(nowrap
    ? { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }
    : { wordBreak: 'keep-all' }),
  '&:last-of-type': { borderRight: 'none' },
}));

// 상품명 셀: 좌측 정렬 텍스트 + 우측 펼침 아이콘
export const PTGroupNameCell = styled(PTCell)({
  justifyContent: 'space-between',
  gap: '8px',
});

export const PTExpandIcon = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  color: '#4ab3bc',
});

// 금액 컬럼 헤더 라벨 + info 아이콘 inline 정렬 (계산식 툴팁용)
export const PTHeadLabel = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
});

export const PTMemberLink = styled(Box, {
  shouldForwardProp: prop => prop !== 'clickable',
})(({ clickable }) => ({
  fontSize: '14px',
  fontWeight: 500,
  color: clickable ? '#4ab3bc' : '#262626',
  textDecoration: 'underline',
  cursor: clickable ? 'pointer' : 'default',
}));

// 2줄(날짜+시각) 셀 내용
export const PTStack = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  lineHeight: 1.3,
});

export const PTEmpty = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  padding: '40px 0',
  border: '1px solid #d9d9d9',
  borderTop: 'none',
  borderBottomLeftRadius: '8px',
  borderBottomRightRadius: '8px',
  color: '#a6a6a6',
  fontSize: '14px',
});

// ============================================
// Pagination
// ============================================
export const PaginationRow = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginTop: '8px',
});

// ============================================
// 결제 상태 라벨(칩)
// ============================================
// 매출관리 목록에서 결제 상태를 텍스트 대신 칩으로 렌더한다.
// 배경은 행 톤(ROW_TONE_BG)보다 진하게 잡아 행 위에서도 칩이 떠 보이게 한다.
const STATUS_CHIP_COLORS = {
  refund: { bg: '#fbe0da', fg: '#c0392b' }, // 환불
  outstanding: { bg: '#fde8cc', fg: '#b26a00' }, // 미수금결제 / 미수 잔액
  change: { bg: '#e3eefb', fg: '#2b6cb0' }, // 변경결제
  canceled: { bg: '#e8e8e8', fg: '#6b6b6b' }, // 취소
  paid: { bg: '#e6f2f3', fg: '#2f8f97' }, // 결제
};

export const StatusChip = styled('span', {
  shouldForwardProp: prop => prop !== 'chipTone',
})(({ chipTone }) => {
  const c = STATUS_CHIP_COLORS[chipTone] || STATUS_CHIP_COLORS.paid;
  return {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: '11px',
    background: c.bg,
    color: c.fg,
    fontSize: '12px',
    fontWeight: 600,
    lineHeight: 1.5,
    whiteSpace: 'nowrap',
  };
});
